import { Button, Intent, MenuItem } from "@blueprintjs/core";
import { ItemRenderer, ItemPredicate, Select as SelectInput } from "@blueprintjs/select";
import { concat, debounce, get, isEmpty, merge, uniq } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createTerm, getTerm } from "utils/utils";
import { IInputProps } from "../types";
import { fetchOptions, isQueryReady } from "components/Template";
import { IMetadata, ISelect } from "components/Template/types";
import { doDisabled, doHidden } from "components/Template/utils";
import Validate from "components/Template/Validate";
import Override from "components/Template/Override";
import ChangeHighlight from "components/Template/ChangeHighlight";
import "./Select.scss";

export interface ISelectProps extends IInputProps {
  template: ISelect;
  metadata: IMetadata;
}

const StringSelect = SelectInput.ofType<string>();

const Select = (props: ISelectProps) => {
  const { template, model, result, metadata, mutators } = props;
  const { getValue, handleChange, handleLoading, handleFault } = mutators;
  const fieldPath = mutators.getPath(template.name);

  const [options, setOptions] = useState(template.options ? template.options : []);
  const lastModeledKey = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // this section will fetch the options from the server only when the dependent field values change
  // and the debounce period lapses
  const modeled = useMemo(
    () =>
      uniq(
        concat(
          get(template, ["query", "url", "sources"], []),
          get(template, ["query", "body", "sources"], []),
          get(template, ["query", "transform", "sources"], [])
        )
      ).reduce((m, k) => merge(m, { [k]: getValue(k) }), {}),
    [template, getValue]
  );
  const modeledKey = useMemo(() => JSON.stringify(modeled), [modeled]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getValued = useMemo(() => getValue, [model, result]);
  const value = useMemo(() => {
    const valued = getValued(template.name);
    const option = options.find((v) => String(v.value) === String(valued));
    return get(option, "label", null);
  }, [options, template, getValued]);

  useEffect(() => {
    const currentValue = getValued(template.name);
    const hasMatchingOption = options.some(
      (option) => String(option.value) === String(currentValue) || String(option.label) === String(currentValue)
    );

    if (hasMatchingOption) {
      handleFault?.(fieldPath);
    }
  }, [fieldPath, getValued, handleFault, options, template.name, value]);

  const disabled = !handleChange || doDisabled(props);
  const debounced = useMemo(
    () =>
      debounce(
        (m) => {
          if (template.query) {
            if (!isQueryReady(m, template.query, getValued)) {
              if (!isMountedRef.current) {
                return;
              }
              setOptions(template.options ? template.options : []);
              handleFault?.(fieldPath);
              handleLoading?.(fieldPath, false);
              return;
            }

            fetchOptions(m, template.query, getValued)
              .then((v) => {
                if (!isMountedRef.current) {
                  return;
                }
                const vs = v || [];
                setOptions(vs);

                const currentValue = getValued(template.name);
                const isSelectedValueValid = vs.some(
                  (option) =>
                    String(option.value) === String(currentValue) || String(option.label) === String(currentValue)
                );

                if (!disabled && !isSelectedValueValid) {
                  handleFault?.(fieldPath, "Must select a valid option.");
                } else {
                  handleFault?.(fieldPath);
                }
              })
              .catch((err) => {
                if (!isMountedRef.current) {
                  return;
                }
                handleFault?.(fieldPath, err?.message || err);
              })
              .finally(() => {
                if (!isMountedRef.current) {
                  return;
                }
                handleLoading?.(fieldPath, false);
              });
          }
        },
        Number(process.env.REACT_APP_DEBOUNCE),
        { leading: true, trailing: true }
      ),
    [fieldPath, getValued, handleFault, handleLoading, template, disabled]
  );
  const getOptions = useCallback((m) => debounced(m), [debounced]);
  useEffect(() => {
    if (handleChange && template.query) {
      if (lastModeledKey.current === modeledKey) {
        return;
      }
      lastModeledKey.current = modeledKey;
      handleLoading?.(fieldPath, true);
      getOptions(modeled);
    }
  }, [fieldPath, handleLoading, handleChange, getOptions, template, modeled, modeledKey]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      debounced.cancel();
    };
  }, [debounced]);

  if (doHidden(props)) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  } else {
    if (metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput = metadata.hiddenInput.filter((i) => i === template.name);
    }
  }

  const findOptionValue = (v: string | null) =>
    options.find((o) => o.label === v || String(o.value) === String(v))?.value || "";
  const findOptionLabel = (v: string | null) =>
    options.find((o) => o.label === v || String(o.value) === String(v))?.label || "";

  const labels = options.map((v) => v.label);
  const currentValue = getValue(template.name) as string;
  const label = findOptionLabel(currentValue) || currentValue;
  const storedValue = mutators.getStoredValue ? (mutators.getStoredValue(template.name) as string) : undefined;
  const hasChanged = storedValue !== undefined && storedValue !== currentValue;

  const handleUndo = () => {
    if (storedValue !== undefined && handleChange) {
      handleChange(template.name, storedValue);
    }
  };

  const handleKeep = () => {
    if (mutators.setStoredValue) {
      mutators.setStoredValue(template.name, currentValue);
    }
  };

  const filterItem: ItemPredicate<string> | undefined =
    labels.length > 8
      ? (q, v) => {
          return v.toLowerCase().indexOf(q.toLowerCase()) >= 0;
        }
      : undefined;

  const renderItem: ItemRenderer<string> = (v, { handleClick, modifiers, query }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    const text = isEmpty(query)
      ? v
      : createTerm({ value: v, terms: getTerm({ value: v }, "value", query.toLowerCase()) }, "value");
    return (
      <MenuItem active={modifiers.active} disabled={modifiers.disabled} key={v} text={text} onClick={handleClick} />
    );
  };

  const fault = fieldPath in metadata.faults ? metadata.faults[fieldPath] : "";
  return (
    <Validate
      {...props}
      className="select"
      showLabel
      showInfo
      {...(fault && { intent: Intent.DANGER, helperText: fault })}
    >
      <ChangeHighlight
        changed={hasChanged}
        onUndo={hasChanged ? handleUndo : undefined}
        onKeep={hasChanged ? handleKeep : undefined}
        changePath={mutators.getPath(template.name)}
      >
        <div className="select-container">
          <StringSelect
            key={template.name}
            items={labels}
            filterable={Boolean(filterItem)}
            itemPredicate={filterItem}
            itemRenderer={renderItem}
            {...(handleChange
              ? {
                  onItemSelect: (v) => {
                    handleChange(template.name, findOptionValue(v));
                    handleFault?.(fieldPath);
                  },
                }
              : {
                  onItemSelect: () => {
                    /** disabled */
                  },
                  disabled: true,
                  readOnly: true,
                })}
            noResults={<MenuItem disabled={true} text="No results." />}
            popoverProps={{
              minimal: true,
              popoverClassName: "select-popover",
            }}
            resetOnQuery={false}
            resetOnSelect={false}
            resetOnClose={false}
          >
            <Button id={template.name} text={label} disabled={disabled} rightIcon="caret-down" />
          </StringSelect>
          <Override {...props} />
        </div>
      </ChangeHighlight>
    </Validate>
  );
};

export default Select;
